import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  forwardRef,
} from 'react';
import classNames from 'classnames';

import Button from 'components/partials/button/button';

import { FileContext } from 'containers/admin/clients/client/tabs-components/files-tab/file/file';

import { downloadFile } from 'helpers/download-file';
import { formatDate } from 'helpers/dates-formatter';

import {
  getIconByFileType,
  readebleFileSize,
  getFileFormat,
} from './file-uploader.helper';

import Icons from 'assets/icons';
import type { CurrentUserHolder } from 'api/market-view-api';

interface FileUploaderError {
  message?: string;
}

interface FileValue {
  name?: string;
  size?: number;
  uploadDate?: string;
}

interface FileUploaderProps {
  label?: string;
  acceptableFiles?: string;
  helpText?: string;
  error?: FileUploaderError;
  disabled?: boolean;
  onChange?: (file: File) => void;
  onBlur?: () => void;
  value?: FileValue | File;
  required?: boolean;
  name?: string;
  'data-testid'?: string;
}

const FileUploader = forwardRef<HTMLInputElement, FileUploaderProps>(
  (
    {
      label,
      acceptableFiles,
      helpText,
      error = {} as FileUploaderError,
      disabled,
      onChange,
      value,
      required,
      'data-testid': dataTestId = 'file-uploader',
    },
    ref
  ) => {
    const fileContext = useContext(FileContext);
    const currentUser = fileContext.currentUser ?? fileContext.currentUserId;
    const { clientId, fileId, setLoading } = fileContext;

    const [file, setFile] = useState<FileValue | File | undefined>(
      value || undefined
    );

    const [fileFormat, setFileFormat] = useState('pdf');
    const errorText = error.message;

    const fileInputRef = useRef<HTMLInputElement>(null);

    const setInputRef = (node: HTMLInputElement | null): void => {
      fileInputRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.RefObject<HTMLInputElement | null>).current = node;
      }
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (!e.target.files?.[0]) {
        return;
      }

      setFile(e.target.files[0]);

      if (onChange) {
        onChange(e.target.files[0]);
      }
    };

    const handleFileUploadClick = (e: React.MouseEvent): void => {
      e.preventDefault();
      fileInputRef.current?.click();
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInputRef.current?.click();
      }
    };

    useEffect(() => {
      setFile(value);
    }, [value]);

    useEffect(() => {
      if (file?.name) {
        setFileFormat(getFileFormat(file.name));
      }
    }, [file]);

    const icon = getIconByFileType(fileFormat);

    const fileAsFileValue = file as FileValue | undefined;

    return (
      <div className="flex flex-col">
        {label && (
          <label className="flex font-semibold mt-2">
            {label}
            {required ? (
              <Icons.RequiredIndicator customClass="w-[7px] h-[7px] ml-1 mt-1" />
            ) : null}
          </label>
        )}
        <input
          ref={setInputRef}
          type="file"
          className="hidden"
          onChange={onFileChange}
          accept={acceptableFiles}
          data-testid={dataTestId}
        />
        {!file?.name ? (
          <>
            <div
              className={classNames(
                'cursor-pointer bg-navy-50 h-24 flex flex-col items-center justify-center rounded-md border border-dashed',
                {
                  'border-critical-500 !border-solid': errorText,
                  'border-navy-200': !errorText,
                }
              )}
              onDrop={(e) => {
                e.preventDefault();
                onChange?.(e.dataTransfer.files[0]);
              }}
              onDragOver={(e) => e.preventDefault()}
              onClick={handleFileUploadClick}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="button"
              aria-label="Upload file"
              data-testid="upload-file-button"
            >
              <Icons.DefaultFile customClass="w-7 h-9" />
              <span>
                <b>Click to upload </b>or drag and drop the file.
              </span>
            </div>
            {helpText && !errorText && (
              <span className="mt-1 text-sm">{helpText}</span>
            )}
            {errorText && (
              <span className="mt-1 text-sm text-critical-500">
                {errorText}
              </span>
            )}
          </>
        ) : (
          <>
            <div
              className={classNames('rounded-md border ', {
                'border-navy-200': !errorText,
                'border-critical-500': errorText,
              })}
            >
              <div className="py-3 px-2 flex items-center justify-between relative">
                <div className="flex items-center">
                  <div className="mr-1">{icon}</div>
                  <div className="flex flex-col">
                    <span className="font-semibold" data-testid="file-name">
                      {file.name}
                    </span>
                    <div className="flex items-center text-xs">
                      <span data-testid="file-size">
                        {readebleFileSize(file.size ?? 0)}
                      </span>

                      {fileAsFileValue?.uploadDate && (
                        <>
                          <span className="mx-2">&bull;</span>
                          <span data-testid="file-upload-date">
                            {formatDate(
                              fileAsFileValue.uploadDate,
                              'MMM D, YYYY'
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex pr-3 ml-1">
                  {disabled ? (
                    <Button
                      onClick={() =>
                        downloadFile({
                          fileName: file?.name ?? '',
                          setLoading: setLoading ?? (() => undefined),
                          currentUser: currentUser as CurrentUserHolder,
                          clientId: clientId ?? '',
                          fileId: fileId ?? '',
                        })
                      }
                      size="sm"
                      variant="reserve-outlined"
                      className={classNames({ 'mr-3': !disabled })}
                      type="button"
                    >
                      Download
                    </Button>
                  ) : (
                    <Button
                      onClick={handleFileUploadClick}
                      size="sm"
                      variant="reserve-outlined"
                    >
                      Replace file
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {!!errorText && (
              <span className="mt-1 text-sm text-critical-500">
                {errorText}
              </span>
            )}
          </>
        )}
      </div>
    );
  }
);

export default FileUploader;
